import {useEffect, useState, useCallback, useRef} from 'react'
import {useParams, useNavigate} from 'react-router-dom'
import {KTSVG} from '../../../_gori/helpers/components/KTSVG'
import {ROUTES} from '../../../_gori/constants'
import {SeoHead} from '../../modules/seo'
import {sanitizeHTML} from './utils/sanitize'
import {formatDate} from './utils/dateFormatter'

type BlogPost = {
  id: number
  title: string
  slug: string
  content: string
  excerpt?: string
  meta_title?: string
  meta_description?: string
  tags?: string[]
  featured_image: string | null
  date: string
}

const API_URL = process.env.REACT_APP_API_URL || ''

export function BlogDetailPage() {
  const {slug} = useParams<{slug: string}>()
  const navigate = useNavigate()
  const [post, setPost] = useState<BlogPost | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showFloatingBack, setShowFloatingBack] = useState(false)
  const [isExiting, setIsExiting] = useState(false)
  const abortControllerRef = useRef<AbortController | null>(null)

  const fetchPost = useCallback(async () => {
    if (!slug) return

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()
    setLoading(true)
    setError(null)

    try {
      const res = await fetch(`${API_URL}/api/blog/post?slug=${slug}`, {
        signal: abortControllerRef.current.signal,
      })

      if (!res.ok) {
        throw new Error(res.status === 404 ? 'Post not found' : 'Failed to load post')
      }

      const data: BlogPost = await res.json()
      setPost(data)
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return

      console.error('Error fetching blog post:', err)
      setError(err instanceof Error ? err.message : 'Failed to load blog post')
    } finally {
      setLoading(false)
    }
  }, [slug])

  useEffect(() => {
    fetchPost()

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [fetchPost])

  useEffect(() => {
    const handleScroll = () => {
      const blogHeader = document.querySelector('.blog-header')
      if (blogHeader) {
        const rect = blogHeader.getBoundingClientRect()
        const shouldShow = rect.top < 0

        if (shouldShow && !showFloatingBack) {
          setShowFloatingBack(true)
          setIsExiting(false)
        } else if (!shouldShow && showFloatingBack) {
          setIsExiting(true)
          setTimeout(() => setShowFloatingBack(false), 300)
        }
      }
    }

    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [showFloatingBack])

  if (loading) {
    return (
      <div className='blog-loading'>
        <SeoHead />
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  if (error || !post) {
    return (
      <div className='container py-10'>
        <SeoHead />
        <div className='alert alert-danger text-center' role='alert'>
          {error || 'Post not found'}
        </div>
      </div>
    )
  }

  return (
    <div className='container'>
      <SeoHead
        seo={{
          title: post.meta_title || `${post.title} | ShipBae`,
          description: post.meta_description || post.excerpt || undefined,
          keywords: post.tags?.join(', ') || undefined,
        }}
      />
      {showFloatingBack && (
        <button
          className={`blog-floating-back-btn ${isExiting ? 'exiting' : ''}`}
          onClick={() => navigate(ROUTES.RESOURCE.BLOG)}
          aria-label='Back to blog'
        >
          <KTSVG path='/media/gori/left-chevron-white.svg' svgClassName='h-20px w-20px' />
        </button>
      )}
      <article>
        <div className='blog-detail-header'>
          <div className='row g-5 align-items-center mt-0'>
            <div className='mt-0 col-12 col-md-5'>
              <h1 className='blog-detail-title raleway fw-bolder'>
                {sanitizeHTML(post.title, false)}
              </h1>

              {post.excerpt && (
                <div
                  className='blog-detail-excerpt'
                  dangerouslySetInnerHTML={{__html: sanitizeHTML(post.excerpt)}}
                />
              )}
            </div>

            <div className='mt-0 col-12 col-md-7'>
              {post.featured_image && (
                <img
                  src={post.featured_image}
                  alt={post.title}
                  className='blog-detail-image img-fluid rounded'
                />
              )}
            </div>
          </div>
        </div>

        <div className='blog-detail-content-wrapper'>
          {post.date && <p className='blog-detail-date text-muted'>{formatDate(post.date)}</p>}
          <div
            className='blog-detail-content'
            dangerouslySetInnerHTML={{__html: sanitizeHTML(post.content)}}
          />
        </div>
      </article>
    </div>
  )
}
